import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import {
  ExclamationCircleIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  CheckBadgeIcon,
} from "@heroicons/react/24/solid";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import { translateText } from "../../utils/translation";
import { getCatalog, updateCatalog } from "../../slices/property";
import SelectOrUnselectAll from "../forms/SelectOrUnselectAll";
import Checkbox from "../forms/Checkbox";

export default function Catalog() {
  const dispatch = useAppDispatch();

  // Fetch data from server
  useEffect(() => {
    dispatch(getCatalog());
  }, [dispatch]);

  const [catalogApprovedState, setCatalogApprovedState] = useState<{
    [key: string]: any;
  }>({});
  const [catalogNotApprovedState, setCatalogNotApprovedState] = useState<{
    [key: string]: any;
  }>({});

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { catalog, options } = useAppSelector((state) => state.property);
  const language = user === null ? "EN" : user.language;

  // Functions
  function onCatalogApprovedChange(opt: string) {
    if (opt in catalogApprovedState) {
      setCatalogApprovedState({
        ...catalogApprovedState,
        [opt]: !catalogApprovedState[opt],
      });
      return;
    }
    const candidate = catalogData.filter((item: any) => item.id === opt)[0];
    setCatalogApprovedState({
      ...catalogApprovedState,
      [opt]: !candidate.in_catalog,
    });
  }

  function onCatalogNotApprovedChange(opt: string) {
    if (opt in catalogNotApprovedState) {
      setCatalogNotApprovedState({
        ...catalogNotApprovedState,
        [opt]: !catalogNotApprovedState[opt],
      });
      return;
    }
    const candidate = catalogData.filter((item: any) => item.id === opt)[0];
    setCatalogNotApprovedState({
      ...catalogNotApprovedState,
      [opt]: !candidate.in_catalog,
    });
  }

  async function onCatalogSubmit(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const requestOptions: any[] = [];
    const toRemove: any[] = [];

    Object.entries(catalogApprovedState).forEach(([key, value]) => {
      if (value === false) toRemove.push(key);
      if (value === true) requestOptions.push(key);
    });
    Object.entries(catalogNotApprovedState).forEach(([key, value]) => {
      if (value === false) toRemove.push(key);
      if (value === true) requestOptions.push(key);
    });

    // Check current data of catalog
    catalogData.forEach((item: any) => {
      if (item.in_catalog === true && !toRemove.includes(item.id)) {
        if (!requestOptions.includes(item.id)) {
          requestOptions.push(item.id);
        }
      }
    });

    const res: any = await dispatch(
      updateCatalog({ property: user.property.id, requestOptions }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_catalog", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
    }
  }

  function selectOrUnselectAllApproved(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    const newState: any = {};
    for (let i = 0; i < approvedData.length; i++) {
      newState[approvedData[i].id] = selectAll;
    }
    setCatalogApprovedState(newState);
  }

  function selectOrUnselectAllNotApproved(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    const newState: any = {};
    for (let i = 0; i < notApprovedData.length; i++) {
      newState[notApprovedData[i].id] = selectAll;
    }
    setCatalogNotApprovedState(newState);
    // setCatalogApprovedState({});
  }

  function decideIfChecked(requestOptionId: string, approved: boolean) {
    if (approved) {
      if (requestOptionId in catalogApprovedState)
        return catalogApprovedState[requestOptionId];
      const candidate = catalogData.filter(
        (item: any) => item.id === requestOptionId,
      )[0];
      return candidate.in_catalog;
    }
    if (requestOptionId in catalogNotApprovedState)
      return catalogNotApprovedState[requestOptionId];
    const candidate = catalogData.filter(
      (item: any) => item.id === requestOptionId,
    )[0];
    return candidate.in_catalog;
  }

  // Format rooms data and filter by query param tag
  const catalogData = catalog.data === null ? [] : catalog.data;
  const approvedData = catalogData.filter(
    (item: any) => item.approved === true,
  );
  let approvedHierarchy: any = {};
  approvedData.forEach((item: any) => {
    if (approvedHierarchy[item.category] === undefined) {
      approvedHierarchy[item.category] = [];
    }
    approvedHierarchy[item.category].push(item);
  });
  approvedHierarchy = Object.keys(approvedHierarchy).map(
    (category: string) => ({ category, options: approvedHierarchy[category] }),
  );
  const notApprovedData = catalogData.filter(
    (item: any) => item.approved === false,
  );
  let notApprovedHierarchy: any = {};
  notApprovedData.forEach((item: any) => {
    if (notApprovedHierarchy[item.category] === undefined) {
      notApprovedHierarchy[item.category] = [];
    }
    notApprovedHierarchy[item.category].push(item);
  });
  notApprovedHierarchy = Object.keys(notApprovedHierarchy).map(
    (category: string) => ({
      category,
      options: notApprovedHierarchy[category],
    }),
  );

  return (
    <Template>
      {/* Header */}
      <Header text={translateText("catalog_title", language)} />

      {/* Loading spinner */}
      {catalog.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            {catalogData.length > 0 ? (
              <div className="rounded-lg p-8 overflow-x-auto drop-shadow scrollbar-invisible mx-1 mb-1 bg-white grid grid-cols-1 gap-y-8">
                {/* Approved */}
                <div className="flex w-full">
                  <HandThumbUpIcon className="h-5 w-5 text-info mr-2 my-auto" />
                  <span className="text-black text-lg font-medium">
                    {translateText("approved", language)}
                  </span>
                </div>
                <SelectOrUnselectAll
                  handler={selectOrUnselectAllApproved}
                  language={language}
                />
                {approvedHierarchy
                  .filter((x: any) => x.category in options.category)
                  .map((category: any) => (
                    <div
                      key={category.category}
                      className="grid grid-cols-1 gap-y-8"
                    >
                      <div>
                        <label className="mb-1 block text-left text-sm gotham-bold text-black">
                          {options.category[category.category][language]}
                        </label>
                      </div>
                      <div className="grid lg:grid-cols-5 md:grid-cols-4 sm:grid-cols-3 grid-cols-1 gap-2">
                        {category.options.map((opt: any) => (
                          <Checkbox
                            key={opt.id}
                            id={opt.id}
                            onChange={onCatalogApprovedChange}
                            checked={decideIfChecked(opt.id, true)}
                            label={opt.translation}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                <div className="border border-b-2 w-full" />

                {/* Not approved */}
                <div className="flex w-full">
                  <HandThumbDownIcon className="h-5 w-5 text-info mr-2 my-auto" />
                  <span className="text-black text-lg font-medium">
                    {translateText("not_approved", language)}
                  </span>
                </div>
                {notApprovedData.length > 0 && (
                  <>
                    <SelectOrUnselectAll
                      handler={selectOrUnselectAllNotApproved}
                      language={language}
                    />
                    {notApprovedHierarchy.map((category: any) => (
                      <div
                        key={category.category}
                        className="grid grid-cols-1 gap-y-8"
                      >
                        <div>
                          <label className="mb-1 block text-left text-sm gotham-bold text-black">
                            {options.category[category.category][language]}
                          </label>
                        </div>
                        <div className="grid lg:grid-cols-5 md:grid-cols-4 sm:grid-cols-3 grid-cols-1 gap-2">
                          {category.options.map((opt: any) => (
                            <Checkbox
                              key={opt.id}
                              id={opt.id}
                              onChange={onCatalogNotApprovedChange}
                              checked={decideIfChecked(opt.id, false)}
                              label={opt.translation}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )}
                <div className="border border-b-2 w-full" />

                <div className="grid grid-cols-1 mt-10">
                  <Button
                    type="button"
                    className={`${getButtonStyle("solid", "success")}`}
                    onClick={onCatalogSubmit}
                  >
                    <CheckBadgeIcon
                      className={`h-5 w-5 text-white mr-1`}
                      aria-hidden="true"
                    />
                    {translateText("save_catalog", language)}
                  </Button>
                </div>
              </div>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_catalog", language)}
                subtext={translateText("no_catalog_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
